import { QueryClient, QueryKey } from 'react-query'

export interface OptimisticChangeResult<T> {
    data: T | undefined
    rollback: (current: T | undefined) => T | undefined
}

export interface OptimisticChange<TResponse = unknown> {
    queryKey: QueryKey
    apply: (current: unknown) => OptimisticChangeResult<unknown>
    reconcile?: (current: unknown, response: TResponse) => unknown
}

interface AppliedChange {
    queryKey: QueryKey
    apply: (current: unknown) => OptimisticChangeResult<unknown>
    reconcile?: (current: unknown, response: unknown) => unknown
    rollback: (current: unknown) => unknown
}

interface QueryLayers {
    queryKey: QueryKey
    layers: AppliedChange[]
}

export interface OptimisticTransaction<TResponse = unknown> {
    queryClient: QueryClient
    changes: AppliedChange[]
    /** Carries the response type for reconcileOptimisticTransaction without adding runtime state. */
    response?: TResponse
}

const layersByClient = new WeakMap<QueryClient, Map<string, QueryLayers>>()

const queryHash = (queryKey: QueryKey) => JSON.stringify(queryKey)

const getQueryLayers = (queryClient: QueryClient, queryKey: QueryKey) => {
    let clientLayers = layersByClient.get(queryClient)
    if (!clientLayers) {
        clientLayers = new Map()
        layersByClient.set(queryClient, clientLayers)
    }
    const hash = queryHash(queryKey)
    let queryLayers = clientLayers.get(hash)
    if (!queryLayers) {
        queryLayers = { queryKey, layers: [] }
        clientLayers.set(hash, queryLayers)
    }
    return { clientLayers, hash, queryLayers }
}

/**
 * Builds the common form of an optimistic cache write. The transaction manager removes and replays
 * later layers before rollback, so the default can restore this layer's prior value. Domain changes
 * that share a cache with unrelated writes can provide a targeted rollback instead.
 */
export const optimisticQueryChange = <T, TResponse = unknown>(
    queryKey: QueryKey,
    update: (current: T | undefined) => T | undefined,
    options: {
        rollback?: (current: T | undefined, previous: T | undefined, optimistic: T | undefined) => T | undefined
        reconcile?: (current: T | undefined, response: TResponse) => T | undefined
    } = {}
): OptimisticChange<TResponse> => ({
    queryKey,
    apply: (untypedPrevious) => {
        const previous = untypedPrevious as T | undefined
        const optimistic = update(previous)
        return {
            data: optimistic,
            rollback: (untypedCurrent) => {
                const current = untypedCurrent as T | undefined
                return options.rollback ? options.rollback(current, previous, optimistic) : previous
            },
        }
    },
    reconcile: options.reconcile
        ? (current, response) => options.reconcile?.(current as T | undefined, response)
        : undefined,
})

/** Applies every cache change immediately and records enough information to settle it later. */
export const beginOptimisticTransaction = <TResponse = unknown>(
    queryClient: QueryClient,
    changes: OptimisticChange<TResponse>[]
): OptimisticTransaction<TResponse> => {
    const appliedChanges = changes.map((change) => {
        const current = queryClient.getQueryData(change.queryKey)
        const applied = change.apply(current)
        const layer: AppliedChange = {
            queryKey: change.queryKey,
            apply: change.apply,
            reconcile: change.reconcile as AppliedChange['reconcile'],
            rollback: applied.rollback,
        }
        const { queryLayers } = getQueryLayers(queryClient, change.queryKey)
        queryLayers.layers.push(layer)
        if (applied.data !== undefined) queryClient.setQueryData(change.queryKey, applied.data)
        return layer
    })
    return { queryClient, changes: appliedChanges }
}

const settleChange = (transaction: OptimisticTransaction, change: AppliedChange, response?: unknown) => {
    const { queryClient } = transaction
    const { clientLayers, hash, queryLayers } = getQueryLayers(queryClient, change.queryKey)
    const index = queryLayers.layers.indexOf(change)
    if (index === -1) return

    const laterLayers = queryLayers.layers.slice(index + 1)
    let current = queryClient.getQueryData(change.queryKey)

    // Remove later guesses temporarily so this layer can settle against the state it originally
    // followed. Reapplying them below regenerates their rollback against the new authoritative base.
    for (let i = laterLayers.length - 1; i >= 0; i -= 1) current = laterLayers[i].rollback(current)

    if (response === undefined) current = change.rollback(current)
    else if (change.reconcile) current = change.reconcile(current, response)

    queryLayers.layers.splice(index, 1)
    for (const later of laterLayers) {
        const replayed = later.apply(current)
        later.rollback = replayed.rollback
        current = replayed.data
    }

    if (current !== undefined) queryClient.setQueryData(change.queryKey, current)
    if (queryLayers.layers.length === 0) clientLayers.delete(hash)
    if (clientLayers.size === 0) layersByClient.delete(queryClient)
}

/** Drops this transaction's guesses and replays any optimistic work queued behind them. */
export const rollbackOptimisticTransaction = (transaction?: OptimisticTransaction) => {
    if (!transaction) return
    for (const change of [...transaction.changes].reverse()) settleChange(transaction, change)
}

/**
 * Commits this transaction, optionally replacing its guess with authoritative response data, then
 * replays optimistic work queued behind it.
 */
export const reconcileOptimisticTransaction = <TResponse>(
    transaction: OptimisticTransaction<TResponse> | undefined,
    response: TResponse
) => {
    if (!transaction) return
    for (const change of transaction.changes) settleChange(transaction, change, response)
}
